import { useEffect, useState } from 'react'
import { lumi } from '@/lib/lumi'
import {Sprout, Search, Loader2, X, TreePine, Camera, MapPin, CheckCircle2, Clock, AlertTriangle, Filter, ChevronDown, ImageOff, MessageCircleDashed as MessageCircle, Bell, Phone, Copy, ExternalLink, CalendarClock, Save, Ban} from 'lucide-react'
import type { Solicitacao, Configuracao } from '@/types'
import { formatarData } from '@/utils/protocolo'
import toast from 'react-hot-toast'

const BRAND = '#0f3b50'

// ─────────────────────────────────────────────────────────────────────────────
// Tipos internos
// ─────────────────────────────────────────────────────────────────────────────
interface DadosReplantio {
  replantioData: string
  replantioResponsavel: string
  replantioEndereco: string
  replantioTipoArvore: string
  replantioFotos: string[]
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Calcula dias restantes (pode ser negativo se vencido) até a data-limite (YYYY-MM-DD). */
function calcularDiasRestantes(prazo: string): number {
  const hoje = new Date()
  hoje.setHours(0, 0, 0, 0)
  const [ano, mes, dia] = prazo.split('-').map(Number)
  const dataPrazo = new Date(ano, (mes || 1) - 1, dia || 1)
  dataPrazo.setHours(0, 0, 0, 0)
  const diffMs = dataPrazo.getTime() - hoje.getTime()
  return Math.round(diffMs / (1000 * 60 * 60 * 24))
}

function formatarDataBR(prazo: string): string {
  const [ano, mes, dia] = prazo.split('-')
  if (!ano || !mes || !dia) return prazo
  return `${dia}/${mes}/${ano}`
}

/** Texto do bloco de prazo, recalculado sempre que chamado. */
function textoPrazo(sol: Solicitacao): string {
  const prazo = (sol as any).prazoReplantio as string | undefined
  if (!prazo) {
    return '⚠️ O prazo para regularização é de *15 (quinze) dias* a contar do recebimento desta mensagem. O não cumprimento poderá acarretar as penalidades previstas na legislação municipal.'
  }
  const dias = calcularDiasRestantes(prazo)
  const dataFormatada = formatarDataBR(prazo)
  if (dias > 1) {
    return `⏳ Faltam *${dias} dias* para o prazo final de regularização do replantio, com vencimento em *${dataFormatada}*. O não cumprimento dentro do prazo poderá acarretar as penalidades previstas na legislação municipal.`
  }
  if (dias === 1) {
    return `⏳ Falta *1 dia* para o prazo final de regularização do replantio, com vencimento em *${dataFormatada}*. O não cumprimento dentro do prazo poderá acarretar as penalidades previstas na legislação municipal.`
  }
  if (dias === 0) {
    return `🚨 *Hoje (${dataFormatada}) é o último dia* do prazo para regularização do replantio. Evite penalidades e regularize ainda hoje.`
  }
  const atraso = Math.abs(dias)
  return `🚨 *Prazo vencido há ${atraso} dia${atraso > 1 ? 's' : ''}* (vencimento em ${dataFormatada}). Regularize com urgência para evitar as penalidades previstas na legislação municipal.`
}

function gerarMensagemAlerta(sol: Solicitacao): string {
  const arvoreReplantio =
    (sol as any).qualArvoreReplantio ||
    (sol as any).tipoArvoreReplantada ||
    sol.tipoArvore ||
    'muda a ser definida'
  const endereco = [
    sol.ruaNome,
    sol.numero ? `nº ${sol.numero}` : '',
    sol.bairroNome,
  ].filter(Boolean).join(', ')

  const prazo = (sol as any).prazoReplantio as string | undefined

  // Quando há um prazo definido/atualizado, a mensagem abre informando a nova data
  const abertura = prazo
    ? `Olá *${sol.nomeCompleto}*,

Conforme o protocolo *${sol.protocolo}*, que foi atualizado, segue a *nova data prevista para o replantio*: *${formatarDataBR(prazo)}*.`
    : `Prezado(a) *${sol.nomeCompleto}*,

Informamos que, até o momento, *não foi registrado nenhum plantio de muda* referente ao protocolo *${sol.protocolo}*.`

  return `${abertura}

Conforme os termos da *Lei Municipal nº 426/2018*, a árvore removida no endereço *${endereco}* deverá ser substituída pelo plantio de:

🌱 *${arvoreReplantio}*

${textoPrazo(sol)}

Após realizar o plantio, entre em contato com a Secretaria de Meio Ambiente para registrar a conclusão e evitar autuações.

_Esta é uma mensagem automática do *Sistema GEOA – Gestão Online Ambiental* da Prefeitura Municipal de São José do Seridó/RN._`
}

function normalizarTelefone(tel: string): string {
  const digits = tel.replace(/\D/g, '')
  if (digits.length === 11 && !digits.startsWith('55')) return `55${digits}`
  if (digits.length === 10 && !digits.startsWith('55')) return `55${digits}`
  return digits
}

// ─────────────────────────────────────────────────────────────────────────────
// Modal Alerta WhatsApp
// ─────────────────────────────────────────────────────────────────────────────
interface ModalAlertaProps {
  sol: Solicitacao
  onClose: () => void
  onPrazoSalvo: (prazo: string) => Promise<void>
}

function ModalAlerta({ sol, onClose, onPrazoSalvo }: ModalAlertaProps) {
  const [prazo, setPrazo] = useState((sol as any).prazoReplantio || '')
  const [salvandoPrazo, setSalvandoPrazo] = useState(false)
  const solAtual = { ...sol, prazoReplantio: prazo } as Solicitacao
  const [mensagem, setMensagem] = useState(() => gerarMensagemAlerta(solAtual))
  const telefone = sol.telefone || ''
  const telNormalizado = normalizarTelefone(telefone)
  const telValido = telNormalizado.length >= 12

  const diasRestantes = prazo ? calcularDiasRestantes(prazo) : null

  // Recalcula a mensagem automaticamente sempre que o prazo mudar
  useEffect(() => {
    setMensagem(gerarMensagemAlerta({ ...sol, prazoReplantio: prazo } as Solicitacao))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prazo])

  const whatsappUrl = `https://wa.me/${telNormalizado}?text=${encodeURIComponent(mensagem)}`

  const copiarMensagem = () => {
    navigator.clipboard.writeText(mensagem)
      .then(() => toast.success('Mensagem copiada!'))
      .catch(() => toast.error('Erro ao copiar.'))
  }

  const handleSalvarPrazo = async () => {
    if (!prazo) { toast.error('Selecione uma data de prazo.'); return }
    setSalvandoPrazo(true)
    try {
      await onPrazoSalvo(prazo)
      toast.success('Prazo de replantio guardado!')
    } catch { toast.error('Erro ao guardar o prazo.') }
    finally { setSalvandoPrazo(false) }
  }

  const handleEnviarClick = () => {
    if (!telefone) { toast.error('Telefone não cadastrado nesta solicitação.'); return }
    if (prazo && prazo !== ((sol as any).prazoReplantio || '')) {
      onPrazoSalvo(prazo).catch(() => {})
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4 py-6"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-xl border border-gray-100 max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#dcfce7' }}>
              <Bell className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">Alerta de Replantio — WhatsApp</h2>
              <p className="text-xs text-gray-400 font-mono">{sol.protocolo} · {sol.nomeCompleto}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-2xl transition">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1">
          {/* Prazo de replantio (calendário) */}
          <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <CalendarClock className="w-4 h-4" style={{ color: BRAND }} />
              <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                Prazo definido para o replantio
              </label>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="date"
                value={prazo}
                onChange={e => setPrazo(e.target.value)}
                className="flex-1 px-3.5 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none bg-white"
              />
              <button
                type="button"
                onClick={handleSalvarPrazo}
                disabled={salvandoPrazo || !prazo}
                className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-2xl text-white disabled:opacity-50 transition"
                style={{ backgroundColor: BRAND }}
              >
                {salvandoPrazo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Guardar prazo
              </button>
            </div>
            {prazo && diasRestantes !== null && (
              <p className={`text-xs font-semibold mt-2 ${
                diasRestantes < 0 ? 'text-red-600' : diasRestantes <= 3 ? 'text-amber-600' : 'text-green-700'
              }`}>
                {diasRestantes > 1 && `⏳ Faltam ${diasRestantes} dias para o vencimento (${formatarDataBR(prazo)}).`}
                {diasRestantes === 1 && `⏳ Falta 1 dia para o vencimento (${formatarDataBR(prazo)}).`}
                {diasRestantes === 0 && `🚨 O prazo vence hoje (${formatarDataBR(prazo)}).`}
                {diasRestantes < 0 && `🚨 Prazo vencido há ${Math.abs(diasRestantes)} dia${Math.abs(diasRestantes) > 1 ? 's' : ''} (venceu em ${formatarDataBR(prazo)}).`}
              </p>
            )}
            <p className="text-[10px] text-gray-500 mt-1.5">
              A contagem de dias é recalculada automaticamente sempre que esta mensagem for reaberta ou enviada.
            </p>
          </div>

          {/* Número de destino */}
          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#e8f1f5' }}>
              <Phone className="w-4 h-4" style={{ color: '#0f3b50' }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Número de destino</p>
              <p className="text-sm font-bold text-gray-900">
                {telefone || <span className="text-red-500 font-medium">Telefone não cadastrado</span>}
              </p>
              {!telValido && telefone && (
                <p className="text-xs text-amber-600 mt-0.5">⚠️ Número pode estar incompleto. Verifique antes de enviar.</p>
              )}
              {!telefone && (
                <p className="text-xs text-red-500 mt-0.5">Este processo não possui telefone cadastrado. Verifique a solicitação.</p>
              )}
            </div>
          </div>

          {/* Pré-visualização / edição da mensagem */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Mensagem <span className="text-gray-400 font-normal normal-case">(editável)</span>
              </label>
              <button
                type="button"
                onClick={() => setMensagem(gerarMensagemAlerta(sol))}
                className="text-xs text-gray-400 hover:text-gray-600 transition underline"
              >
                Restaurar padrão
              </button>
            </div>
            <textarea
              value={mensagem}
              onChange={e => setMensagem(e.target.value)}
              rows={14}
              className="w-full px-4 py-3 rounded-2xl border border-gray-200 text-sm text-gray-800 focus:outline-none resize-none leading-relaxed"
              style={{ fontFamily: 'inherit' }}
            />
            <p className="text-[10px] text-gray-400 mt-1">
              * O texto em negrito usa *asteriscos* — formatação nativa do WhatsApp.
            </p>
          </div>
        </div>

        {/* Ações */}
        <div className="px-6 py-4 border-t border-gray-100 flex gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-2xl hover:bg-gray-50 transition"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={copiarMensagem}
            className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 text-gray-700 text-sm font-medium rounded-2xl hover:bg-gray-50 transition"
          >
            <Copy className="w-4 h-4" />
            <span className="hidden sm:inline">Copiar</span>
          </button>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleEnviarClick}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-semibold rounded-2xl transition ${
              !telefone
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed pointer-events-none'
                : 'text-white hover:opacity-90'
            }`}
            style={telefone ? { backgroundColor: '#25D366' } : {}}
          >
            <MessageCircle className="w-4 h-4" />
            Enviar pelo WhatsApp
            <ExternalLink className="w-3 h-3 opacity-70" />
          </a>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Modal Registar Replantio
// ─────────────────────────────────────────────────────────────────────────────
interface ModalReplantioProps {
  sol: Solicitacao
  onClose: () => void
  onSalvar: (dados: DadosReplantio) => Promise<void>
  loading: boolean
}

function ModalReplantio({ sol, onClose, onSalvar, loading }: ModalReplantioProps) {
  const [data, setData]               = useState((sol as any).replantioData || '')
  const [responsavel, setResponsavel] = useState((sol as any).replantioResponsavel || '')
  const [endereco, setEndereco]       = useState(
    (sol as any).replantioEndereco ||
    (sol as any).enderecoReplantio ||
    [sol.ruaNome, sol.numero, sol.bairroNome].filter(Boolean).join(', ')
  )
  const [tipoArvore, setTipoArvore]   = useState(
    (sol as any).replantioTipoArvore ||
    (sol as any).tipoArvoreReplantada ||
    sol.tipoArvore || ''
  )
  const [fotos, setFotos]             = useState<string[]>((sol as any).replantioFotos || [])
  const [uploadingFoto, setUploadingFoto] = useState(false)
  const [fotoPreview, setFotoPreview]     = useState<string | null>(null)

  const inp = 'w-full px-3.5 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:border-[#0f3b50] focus:ring-2 focus:ring-[#0f3b50]/10'

  const handleFotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return
    if (fotos.length >= 3) { toast.error('Máximo de 3 fotos permitido.'); return }
    const file = files[0]
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Formato inválido. Use JPG, PNG ou WEBP.')
      return
    }
    setUploadingFoto(true)
    try {
      const results = await lumi.tools.file.upload([file])
      const ok = results.find(r => r.fileUrl)
      if (ok?.fileUrl) {
        setFotos(prev => [...prev, ok.fileUrl!])
        toast.success('Foto adicionada!')
      } else {
        toast.error('Erro ao fazer upload da foto.')
      }
    } catch { toast.error('Erro ao fazer upload.') }
    finally { setUploadingFoto(false); e.target.value = '' }
  }

  const removerFoto = (idx: number) => setFotos(prev => prev.filter((_, i) => i !== idx))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!data.trim())        { toast.error('Informe a data do plantio.'); return }
    if (!responsavel.trim()) { toast.error('Informe quem realizou o plantio.'); return }
    if (!endereco.trim())    { toast.error('Informe o endereço do replantio.'); return }
    if (!tipoArvore.trim())  { toast.error('Informe o tipo/espécie da árvore plantada.'); return }
    onSalvar({ replantioData: data, replantioResponsavel: responsavel, replantioEndereco: endereco, replantioTipoArvore: tipoArvore, replantioFotos: fotos })
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4 py-6"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg border border-gray-100 max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white rounded-t-3xl border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#d1fae5' }}>
              <Sprout className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">
                {(sol as any).replantioRegistado ? 'Editar Registo de Replantio' : 'Registar Replantio'}
              </h2>
              <p className="text-xs text-gray-400 font-mono">{sol.protocolo} — {sol.nomeCompleto}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-2xl transition">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
          {/* Info da solicitação */}
          <div className="bg-green-50 border border-green-100 rounded-2xl p-4 flex items-start gap-3">
            <TreePine className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-green-800">
              <p className="font-semibold mb-0.5">Árvore a ser removida</p>
              <p>{sol.tipoArvore} · {sol.estadoArvore} · Porte: {(sol as any).porte || '—'}</p>
              <p className="text-green-600 mt-0.5">
                {[sol.ruaNome, sol.numero, sol.bairroNome].filter(Boolean).join(', ')}
              </p>
            </div>
          </div>

          {/* Campos principais */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Data do Plantio <span className="text-red-500">*</span>
              </label>
              <input type="date" value={data} onChange={e => setData(e.target.value)} className={inp} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Tipo / Espécie Plantada <span className="text-red-500">*</span>
              </label>
              <input value={tipoArvore} onChange={e => setTipoArvore(e.target.value)}
                placeholder="Ex: Ipê Amarelo, Aroeira..." className={inp} />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              Responsável pelo Plantio <span className="text-red-500">*</span>
            </label>
            <input value={responsavel} onChange={e => setResponsavel(e.target.value)}
              placeholder="Nome de quem realizou o plantio" className={inp} />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              <MapPin className="w-3 h-3 inline mr-1" />Endereço do Replantio <span className="text-red-500">*</span>
            </label>
            <input value={endereco} onChange={e => setEndereco(e.target.value)}
              placeholder="Rua, número, bairro..." className={inp} />
          </div>

          {/* Upload de fotos */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Fotos do Replantio <span className="text-gray-400 font-normal normal-case">(opcional, máx. 3)</span>
              </label>
              <span className="text-xs text-gray-400">{fotos.length}/3</span>
            </div>

            {fotos.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mb-3">
                {fotos.map((url, idx) => (
                  <div key={idx} className="relative group rounded-2xl overflow-hidden border border-gray-100" style={{ aspectRatio: '1/1' }}>
                    <img src={url} alt={`Foto ${idx + 1}`} className="w-full h-full object-cover"
                      onClick={() => setFotoPreview(url)} />
                    <button type="button" onClick={() => removerFoto(idx)}
                      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500/90 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition text-xs">
                      ✕
                    </button>
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition cursor-pointer"
                      onClick={() => setFotoPreview(url)} />
                  </div>
                ))}
              </div>
            )}

            {fotos.length < 3 && (
              <label className={`flex flex-col items-center justify-center gap-2 w-full py-5 border-2 border-dashed rounded-2xl cursor-pointer transition ${
                uploadingFoto ? 'border-gray-200 bg-gray-50 cursor-not-allowed' : 'border-green-200 hover:border-green-400 hover:bg-green-50/40'
              }`}>
                <input type="file" accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleFotoUpload} disabled={uploadingFoto} className="hidden" />
                {uploadingFoto
                  ? <Loader2 className="w-5 h-5 text-green-500 animate-spin" />
                  : <Camera className="w-5 h-5 text-green-500" />
                }
                <span className="text-xs text-gray-500">{uploadingFoto ? 'A carregar…' : 'Clique para adicionar foto'}</span>
                <span className="text-[10px] text-gray-400">JPG, PNG ou WEBP</span>
              </label>
            )}
          </div>

          {/* Ações */}
          <div className="flex gap-3 pt-1 border-t border-gray-100">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-2xl hover:bg-gray-50 transition">
              Cancelar
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 py-2.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-semibold rounded-2xl transition flex items-center justify-center gap-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sprout className="w-4 h-4" />}
              {loading ? 'A guardar…' : 'Guardar Replantio'}
            </button>
          </div>
        </form>
      </div>

      {/* Lightbox */}
      {fotoPreview && (
        <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center"
          onClick={() => setFotoPreview(null)}>
          <img src={fotoPreview} alt="Preview" className="max-w-[90vw] max-h-[85vh] rounded-2xl shadow-2xl" />
          <button className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white"
            onClick={() => setFotoPreview(null)}>✕</button>
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Modal Cancelar Replantio (com justificativa)
// ─────────────────────────────────────────────────────────────────────────────
interface ModalCancelarProps {
  sol: Solicitacao
  onClose: () => void
  onConfirmar: (justificativa: string) => Promise<void>
  loading: boolean
}

function ModalCancelarReplantio({ sol, onClose, onConfirmar, loading }: ModalCancelarProps) {
  const [justificativa, setJustificativa] = useState((sol as any).replantioCanceladoJustificativa || '')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!justificativa.trim()) { toast.error('Informe a justificativa do cancelamento.'); return }
    onConfirmar(justificativa.trim())
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4 py-6"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg border border-gray-100 max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white rounded-t-3xl border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#fee2e2' }}>
              <Ban className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">Cancelar Replantio</h2>
              <p className="text-xs text-gray-400 font-mono">{sol.protocolo} — {sol.nomeCompleto}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-2xl transition">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div className="bg-red-50 border border-red-100 rounded-2xl p-4 flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-red-800 leading-relaxed">
              Esta ação marcará o replantio deste processo como <strong>cancelado</strong>. A justificativa informada
              será exibida ao cidadão no Portal do Cidadão, no histórico do processo.
            </p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              Justificativa do Cancelamento <span className="text-red-500">*</span>
            </label>
            <textarea
              value={justificativa}
              onChange={e => setJustificativa(e.target.value)}
              rows={5}
              placeholder="Explique o motivo do cancelamento do replantio (ex.: área incompatível, mudança de local autorizada, indisponibilidade de espécie, etc.)"
              className="w-full px-3.5 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 resize-none"
            />
          </div>

          <div className="flex gap-3 pt-1 border-t border-gray-100">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-2xl hover:bg-gray-50 transition">
              Voltar
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-semibold rounded-2xl transition flex items-center justify-center gap-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
              {loading ? 'A cancelar…' : 'Confirmar Cancelamento'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Galeria de fotos do replantio (exibição no card)
// ─────────────────────────────────────────────────────────────────────────────
function GaleriaFotos({ fotos }: { fotos: string[] }) {
  const [preview, setPreview] = useState<string | null>(null)
  if (!fotos || fotos.length === 0) return null
  return (
    <>
      <div className="flex gap-1.5 mt-2 flex-wrap">
        {fotos.map((url, idx) => (
          <div key={idx} className="w-14 h-14 rounded-xl overflow-hidden border border-gray-100 cursor-pointer group relative"
            onClick={() => setPreview(url)}>
            <img src={url} alt={`Foto ${idx + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition" />
          </div>
        ))}
      </div>
      {preview && (
        <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center" onClick={() => setPreview(null)}>
          <img src={preview} alt="Preview" className="max-w-[90vw] max-h-[85vh] rounded-2xl shadow-2xl" />
          <button className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white" onClick={() => setPreview(null)}>✕</button>
        </div>
      )}
    </>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// PÁGINA PRINCIPAL — Replantio
// ─────────────────────────────────────────────────────────────────────────────
type FiltroAba = 'todos' | 'pendentes' | 'registados'

export default function ReplantioPage() {
  const [solicitacoes, setSolicitacoes] = useState<Solicitacao[]>([])
  const [loading, setLoading]           = useState(true)
  const [search, setSearch]             = useState('')
  const [aba, setAba]                   = useState<FiltroAba>('todos')
  const [showFiltros, setShowFiltros]   = useState(false)
  const [filtroStatus, setFiltroStatus] = useState('')
  const [replantioSol, setReplantioSol] = useState<Solicitacao | null>(null)
  const [loadingReplantio, setLoadingReplantio] = useState(false)
  const [alertaSol, setAlertaSol] = useState<Solicitacao | null>(null)
  const [cancelarSol, setCancelarSol] = useState<Solicitacao | null>(null)
  const [loadingCancelar, setLoadingCancelar] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_cfg, setCfg] = useState<Configuracao | null>(null)

  const fetchData = () => {
    setLoading(true)
    Promise.all([
      lumi.entities.solicitacoes.list({ sort: { createdAt: -1 }, limit: 500 }),
      lumi.entities.configuracoes.list({ limit: 1 }),
    ]).then(([solRes, cfgRes]) => {
      // Filtra apenas processos aprovados ou com replantio previsto
      const lista = (solRes.list || []) as Solicitacao[]
      const relevantes = lista.filter(s =>
        s.status === 'Aprovada' ||
        s.status === 'Aguardando Replantio' ||
        s.status === 'Processo Concluído' ||
        (s as any).replantioRegistado
      )
      setSolicitacoes(relevantes)
      setCfg((cfgRes.list?.[0] as Configuracao) || null)
    }).catch(() => {}).finally(() => setLoading(false))
  }

  useEffect(() => { fetchData() }, [])

  const handleSalvarReplantio = async (dados: DadosReplantio) => {
    if (!replantioSol) return
    setLoadingReplantio(true)
    try {
      const now = new Date().toISOString()
      await lumi.entities.solicitacoes.update(replantioSol._id, {
        replantioRegistado: true,
        replantioData: dados.replantioData,
        replantioResponsavel: dados.replantioResponsavel,
        replantioEndereco: dados.replantioEndereco,
        replantioTipoArvore: dados.replantioTipoArvore,
        replantioFotos: dados.replantioFotos,
        updatedAt: now,
      })
      await lumi.entities.historico_status.create({
        solicitacaoId: replantioSol._id,
        protocolo: replantioSol.protocolo,
        status: replantioSol.status,
        responsavel: dados.replantioResponsavel,
        observacoes: `Replantio registado: ${dados.replantioTipoArvore} em ${dados.replantioEndereco} — plantado por ${dados.replantioResponsavel} em ${dados.replantioData}.`,
        creator: 'sistema',
        createdAt: now,
        updatedAt: now,
      })
      toast.success('Replantio registado com sucesso!')
      setReplantioSol(null)
      fetchData()
    } catch { toast.error('Erro ao guardar replantio.') }
    finally { setLoadingReplantio(false) }
  }

  const handleConfirmarCancelamento = async (justificativa: string) => {
    if (!cancelarSol) return
    setLoadingCancelar(true)
    try {
      const now = new Date().toISOString()
      await lumi.entities.solicitacoes.update(cancelarSol._id, {
        replantioCancelado: true,
        replantioCanceladoJustificativa: justificativa,
        replantioCanceladoData: now,
        updatedAt: now,
      })
      await lumi.entities.historico_status.create({
        solicitacaoId: cancelarSol._id,
        protocolo: cancelarSol.protocolo,
        status: cancelarSol.status,
        responsavel: 'Administração',
        observacoes: `Replantio cancelado. Justificativa: ${justificativa}`,
        creator: 'sistema',
        createdAt: now,
        updatedAt: now,
      })
      toast.success('Cancelamento de replantio registado.')
      setCancelarSol(null)
      fetchData()
    } catch { toast.error('Erro ao registar o cancelamento.') }
    finally { setLoadingCancelar(false) }
  }

  // Filtros
  const q = search.toLowerCase()
  const filtradas = solicitacoes.filter(s => {
    const matchSearch = !q ||
      s.protocolo?.toLowerCase().includes(q) ||
      s.nomeCompleto?.toLowerCase().includes(q) ||
      s.cpf?.toLowerCase().includes(q) ||
      s.bairroNome?.toLowerCase().includes(q)
    const matchStatus = !filtroStatus || s.status === filtroStatus
    const matchAba =
      aba === 'todos' ? true :
      aba === 'pendentes' ? !(s as any).replantioRegistado :
      !!(s as any).replantioRegistado
    return matchSearch && matchStatus && matchAba
  })

  const totalPendentes  = solicitacoes.filter(s => !(s as any).replantioRegistado).length
  const totalRegistados = solicitacoes.filter(s => !!(s as any).replantioRegistado).length
  const taxa = solicitacoes.length > 0
    ? Math.round((totalRegistados / solicitacoes.length) * 100)
    : 0

  return (
    <div className="space-y-5">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Área de Replantio</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Gerencie o registo de replantio dos processos aprovados
          </p>
        </div>
        {totalPendentes > 0 && (
          <div className="flex items-center gap-2 px-4 py-2 rounded-2xl border text-sm font-semibold" style={{ borderColor: '#fbbf24', backgroundColor: '#fffbeb', color: '#92400e' }}>
            <Bell className="w-4 h-4" style={{ color: '#d97706' }} />
            <span>{totalPendentes} processo{totalPendentes > 1 ? 's' : ''} aguardando replantio</span>
          </div>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiCard
          label="Processos com Replantio"
          value={solicitacoes.length}
          icon={<TreePine className="w-4 h-4" />}
          color={BRAND}
          bg="#e8f1f5"
        />
        <KpiCard
          label="Pendentes"
          value={totalPendentes}
          icon={<Clock className="w-4 h-4" />}
          color="#d97706"
          bg="#fef3c7"
        />
        <KpiCard
          label="Registados"
          value={totalRegistados}
          icon={<CheckCircle2 className="w-4 h-4" />}
          color="#16a34a"
          bg="#dcfce7"
        />
        <KpiCard
          label="Taxa de Cumprimento"
          value={`${taxa}%`}
          icon={<Sprout className="w-4 h-4" />}
          color="#0f766e"
          bg="#d1fae5"
        />
      </div>

      {/* Abas */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-2xl w-fit">
        {([
          { key: 'todos',      label: 'Todos',      count: solicitacoes.length },
          { key: 'pendentes',  label: 'Pendentes',  count: totalPendentes },
          { key: 'registados', label: 'Registados', count: totalRegistados },
        ] as { key: FiltroAba; label: string; count: number }[]).map(item => (
          <button key={item.key} onClick={() => setAba(item.key)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl transition ${
              aba === item.key
                ? item.key === 'pendentes'
                  ? 'bg-white text-amber-700 shadow-sm'
                  : item.key === 'registados'
                    ? 'bg-white text-green-700 shadow-sm'
                    : 'bg-white text-[#0f3b50] shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}>
            {item.label}
            <span className={`text-xs px-1.5 py-0.5 rounded-lg font-bold ${
              aba === item.key
                ? item.key === 'pendentes' ? 'bg-amber-500 text-white'
                  : item.key === 'registados' ? 'bg-green-600 text-white'
                  : 'bg-[#0f3b50] text-white'
                : 'bg-gray-200 text-gray-600'
            }`}>{item.count}</span>
          </button>
        ))}
      </div>

      {/* Busca + Filtros */}
      <div className="bg-white rounded-3xl border border-gray-100 p-4 shadow-sm space-y-3">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Protocolo, nome, CPF, bairro..."
              className="w-full pl-9 pr-4 py-2 rounded-2xl border border-gray-200 text-sm focus:outline-none"
              onFocus={e => { e.currentTarget.style.borderColor = BRAND; e.currentTarget.style.boxShadow = `0 0 0 2px ${BRAND}20` }}
              onBlur={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.boxShadow = '' }} />
          </div>
          <button onClick={() => setShowFiltros(!showFiltros)}
            className="flex items-center gap-1.5 px-3 py-2 border rounded-2xl text-sm transition"
            style={showFiltros || !!filtroStatus
              ? { borderColor: BRAND, color: BRAND, backgroundColor: '#e8f1f5' }
              : { borderColor: '#e5e7eb', color: '#4b5563' }}>
            <Filter className="w-4 h-4" />
            <span className="hidden sm:inline">Filtros</span>
            <ChevronDown className={`w-3 h-3 transition ${showFiltros ? 'rotate-180' : ''}`} />
          </button>
        </div>
        {showFiltros && (
          <div className="pt-2 border-t border-gray-100">
            <label className="block text-xs font-medium text-gray-500 mb-1">Status do Processo</label>
            <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}
              className="w-full sm:w-64 px-3 py-2 rounded-2xl border border-gray-200 text-sm focus:outline-none bg-white">
              <option value="">Todos os status</option>
              <option value="Aprovada">Aprovada</option>
              <option value="Aguardando Replantio">Aguardando Replantio</option>
              <option value="Processo Concluído">Processo Concluído</option>
            </select>
          </div>
        )}
      </div>

      {/* Lista */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin mr-2" style={{ color: BRAND }} />
          <span className="text-sm text-gray-500">Carregando processos...</span>
        </div>
      ) : filtradas.length === 0 ? (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm text-center py-16">
          <Sprout className="w-10 h-10 text-gray-200 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-400">
            {search || filtroStatus ? 'Nenhum processo encontrado com os filtros aplicados.' : 'Nenhum processo de replantio encontrado.'}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Processos aparecem aqui quando aprovados ou aguardando replantio.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtradas.map(s => {
            const registado = !!(s as any).replantioRegistado
            const cancelado = !!(s as any).replantioCancelado
            const fotos: string[] = (s as any).replantioFotos || []

            return (
              <div key={s._id}
                className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5 hover:shadow-md transition">
                <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                  {/* Ícone de estado */}
                  <div className={`w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 ${
                    registado ? 'bg-green-50' : 'bg-amber-50'
                  }`}>
                    {registado
                      ? <CheckCircle2 className="w-5 h-5 text-green-600" />
                      : <AlertTriangle className="w-5 h-5 text-amber-500" />
                    }
                  </div>

                  {/* Dados do processo */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="text-xs font-mono font-bold text-gray-900">{s.protocolo}</span>
                      <StatusBadge status={s.status} />
                      {registado && (
                        <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200 font-semibold">
                          <CheckCircle2 className="w-2.5 h-2.5" />Replantio Registado
                        </span>
                      )}
                      {cancelado && (
                        <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 font-semibold">
                          <Ban className="w-2.5 h-2.5" />Replantio Cancelado
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-semibold text-gray-800 truncate">{s.nomeCompleto}</p>
                    <div className="flex flex-wrap gap-3 mt-1.5 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <TreePine className="w-3 h-3 text-gray-400" />
                        {s.tipoArvore} · {(s as any).porte || s.estadoArvore}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3 text-gray-400" />
                        {[s.ruaNome, s.numero, s.bairroNome].filter(Boolean).join(', ')}
                      </span>
                    </div>

                    {/* Replantio cancelado */}
                    {cancelado && (
                      <div className="mt-3 bg-red-50 border border-red-100 rounded-2xl p-3 space-y-1">
                        <p className="text-xs font-bold text-red-800 flex items-center gap-1.5">
                          <Ban className="w-3.5 h-3.5" />Replantio Cancelado
                        </p>
                        <p className="text-xs text-red-700 leading-relaxed">{(s as any).replantioCanceladoJustificativa}</p>
                        {(s as any).replantioCanceladoData && (
                          <p className="text-[10px] text-red-500">Cancelado em {formatarData((s as any).replantioCanceladoData)}</p>
                        )}
                      </div>
                    )}

                    {/* Dados do replantio registado */}
                    {registado && (
                      <div className="mt-3 bg-green-50 border border-green-100 rounded-2xl p-3 space-y-1.5">
                        <p className="text-xs font-bold text-green-800 mb-1">Replantio Realizado</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs text-green-900">
                          {(s as any).replantioData && (
                            <span><span className="font-medium text-green-700">Data:</span> {formatarData((s as any).replantioData)}</span>
                          )}
                          {(s as any).replantioTipoArvore && (
                            <span><span className="font-medium text-green-700">Espécie:</span> {(s as any).replantioTipoArvore}</span>
                          )}
                          {(s as any).replantioResponsavel && (
                            <span><span className="font-medium text-green-700">Responsável:</span> {(s as any).replantioResponsavel}</span>
                          )}
                          {(s as any).replantioEndereco && (
                            <span className="col-span-2 sm:col-span-3">
                              <span className="font-medium text-green-700">Local:</span> {(s as any).replantioEndereco}
                            </span>
                          )}
                        </div>
                        {fotos.length > 0 && <GaleriaFotos fotos={fotos} />}
                        {fotos.length === 0 && (
                          <p className="text-[10px] text-green-600 flex items-center gap-1 mt-1">
                            <ImageOff className="w-3 h-3" />Sem fotos registadas
                          </p>
                        )}
                      </div>
                    )}

                    {/* Data de abertura */}
                    <p className="text-xs text-gray-400 mt-2">
                      Aberto em {formatarData(s.createdAt)}
                    </p>
                  </div>

                  {/* Botões de acção */}
                  <div className="flex-shrink-0 flex flex-col gap-2">
                    {!registado && (
                      <button
                        onClick={() => setAlertaSol(s)}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-2xl border transition"
                        style={{ borderColor: '#25D366', backgroundColor: '#f0fdf4', color: '#15803d' }}
                        title="Enviar alerta de replantio pelo WhatsApp"
                      >
                        <MessageCircle className="w-4 h-4" style={{ color: '#25D366' }} />
                        Alerta WhatsApp
                      </button>
                    )}
                    <button onClick={() => setReplantioSol(s)}
                      className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-2xl transition ${
                        registado
                          ? 'bg-green-50 border border-green-200 text-green-700 hover:bg-green-100'
                          : 'text-white hover:opacity-90'
                      }`}
                      style={registado ? {} : { backgroundColor: '#16a34a' }}>
                      <Sprout className="w-4 h-4" />
                      {registado ? 'Editar Registo' : 'Registar Replantio'}
                    </button>
                    {!registado && (
                      <button onClick={() => setCancelarSol(s)}
                        className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-2xl border transition ${
                          cancelado
                            ? 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100'
                            : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                        }`}>
                        <Ban className="w-4 h-4" />
                        {cancelado ? 'Editar Justificativa' : 'Cancelar Replantio'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Modal Registar Replantio */}
      {replantioSol && (
        <ModalReplantio
          sol={replantioSol}
          onClose={() => setReplantioSol(null)}
          onSalvar={handleSalvarReplantio}
          loading={loadingReplantio}
        />
      )}

      {/* Modal Alerta WhatsApp */}
      {alertaSol && (
        <ModalAlerta
          sol={alertaSol}
          onClose={() => setAlertaSol(null)}
          onPrazoSalvo={async (prazo) => {
            await lumi.entities.solicitacoes.update(alertaSol._id, {
              prazoReplantio: prazo,
              updatedAt: new Date().toISOString(),
            })
            setSolicitacoes(prev => prev.map(s => s._id === alertaSol._id ? { ...s, prazoReplantio: prazo } as Solicitacao : s))
            setAlertaSol(prev => prev ? ({ ...prev, prazoReplantio: prazo } as Solicitacao) : prev)
          }}
        />
      )}

      {/* Modal Cancelar Replantio */}
      {cancelarSol && (
        <ModalCancelarReplantio
          sol={cancelarSol}
          onClose={() => setCancelarSol(null)}
          onConfirmar={handleConfirmarCancelamento}
          loading={loadingCancelar}
        />
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Componentes auxiliares
// ─────────────────────────────────────────────────────────────────────────────
function KpiCard({ label, value, icon, color, bg }: {
  label: string; value: string | number; icon: React.ReactNode; color: string; bg: string
}) {
  return (
    <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-gray-500 leading-tight">{label}</span>
        <div className="w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: bg, color }}>
          {icon}
        </div>
      </div>
      <p className="text-2xl font-bold" style={{ color }}>{value}</p>
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    'Aprovada':             'bg-green-50 text-green-700 border-green-200',
    'Aguardando Replantio': 'bg-orange-50 text-orange-700 border-orange-200',
    'Processo Concluído':   'bg-gray-100 text-gray-700 border-gray-300',
  }
  const cls = map[status] || 'bg-gray-50 text-gray-600 border-gray-200'
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${cls}`}>
      {status}
    </span>
  )
}
